import React from "react";
import { useNavigate } from "react-router-dom";

const tiers = [
  {
    name: "Starter",
    investment: "KES 50,000",
    roi: "15%",
    period: "Annual Returns",
    vehicles: "0.5 bike share",
    payout: "Monthly",
    color: "#60a5fa",
    features: ["Monthly reports", "App dashboard", "Guaranteed returns", "Fleet insurance"],
  },
  {
    name: "Growth",
    investment: "KES 150,000",
    roi: "20%",
    period: "Annual Returns",
    vehicles: "1 full bike",
    payout: "Bi-weekly",
    color: "#ffd700",
    featured: true,
    features: ["Real-time tracking", "Priority support", "Bonus on milestones", "Fleet insurance", "Dedicated manager"],
  },
  {
    name: "Enterprise",
    investment: "KES 500,000+",
    roi: "25%",
    period: "Annual Returns",
    vehicles: "3+ vehicles",
    payout: "Weekly",
    color: "#34d399",
    features: ["Dedicated manager", "Custom contracts", "Fleet expansion rights", "Tax optimization", "Board advisory"],
  },
];

const FleetInvestmentSection = () => {
  const navigate = useNavigate();
  return (
    <section id="invest" className="py-24 relative overflow-hidden" style={{ background: "#0d1f3c" }}>
      {/* Background */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{ background: "radial-gradient(ellipse at 80% 50%, #ffd700 0%, transparent 60%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(rgba(255,215,0,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.8) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-5 border"
              style={{ background: "rgba(255,215,0,0.08)", borderColor: "rgba(255,215,0,0.2)" }}
            >
              <span className="text-[#ffd700] text-sm font-semibold">💰 Fleet Investment</span>
            </div>
            <h2
              className="text-4xl lg:text-5xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Own the Fleet.
              <br />
              <span className="text-[#ffd700]">Earn Every Trip.</span>
            </h2>
            <p className="text-white/55 text-lg leading-relaxed mb-8">
              Invest in OnTheGo's delivery fleet and earn passive income from every ride and delivery completed by your vehicles. No driving required — we manage everything.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: "📈", text: "Up to 25% annual ROI" },
                { icon: "🔒", text: "Insured & managed fleet" },
                { icon: "📱", text: "Real-time earnings dashboard" },
                { icon: "💳", text: "Weekly/monthly payouts" },
              ]?.map((item) => (
                <div
                  key={item?.text}
                  className="flex items-center gap-3 p-3 rounded-xl border"
                  style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.07)" }}
                >
                  <span className="text-lg">{item?.icon}</span>
                  <span className="text-white/65 text-sm font-medium">{item?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ROI projection visual */}
          <div
            className="rounded-3xl p-8 border"
            style={{
              background: "linear-gradient(135deg, rgba(255,215,0,0.06) 0%, rgba(10,22,40,0.95) 100%)",
              borderColor: "rgba(255,215,0,0.18)",
            }}
          >
            <div className="text-center mb-8">
              <div className="text-5xl mb-3">📊</div>
              <div className="text-white font-black text-lg mb-1">Sample ROI Projection</div>
              <div className="text-white/35 text-sm">Based on KES 150,000 investment</div>
            </div>
            <div className="space-y-5">
              {[
                { label: "Year 1", amount: "KES 30,000", percent: 22, color: "#ffd700", note: "+20% return" },
                { label: "Year 2", amount: "KES 36,000", percent: 40, color: "#f97316", note: "+24% return" },
                { label: "Year 3", amount: "KES 43,200", percent: 60, color: "#34d399", note: "+29% return" },
              ]?.map((row) => (
                <div key={row?.label}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-white/50 text-sm font-medium">{row?.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${row?.color}18`, color: row?.color }}>
                        {row?.note}
                      </span>
                    </div>
                    <span className="text-white font-bold text-sm">{row?.amount}</span>
                  </div>
                  <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${row?.percent}%`,
                        background: `linear-gradient(90deg, ${row?.color}, ${row?.color}70)`,
                        transition: "width 1.5s ease",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t text-center" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <div className="text-white/30 text-xs">*Projections based on current fleet performance data</div>
            </div>
          </div>
        </div>

        {/* Investment tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers?.map((tier) => (
            <div
              key={tier?.name}
              className="relative rounded-3xl p-8 border transition-all duration-300 hover:scale-[1.03]"
              style={{
                background: tier?.featured
                  ? `linear-gradient(135deg, ${tier?.color}12 0%, rgba(10,22,40,0.95) 100%)`
                  : `linear-gradient(135deg, ${tier?.color}06 0%, rgba(13,31,60,0.85) 100%)`,
                borderColor: tier?.featured ? `${tier?.color}40` : `${tier?.color}20`,
                boxShadow: tier?.featured ? `0 0 40px ${tier?.color}15` : "none",
              }}
            >
              {tier?.featured && (
                <div
                  className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 rounded-full text-xs font-black text-[#0a1628] whitespace-nowrap"
                  style={{ background: `linear-gradient(135deg, ${tier?.color}, ${tier?.color}bb)` }}
                >
                  ★ Most Popular
                </div>
              )}

              <div className="mb-6 pt-2">
                <div className="text-white/40 text-xs uppercase tracking-widest mb-2">{tier?.name}</div>
                <div
                  className="text-5xl font-black mb-1"
                  style={{ color: tier?.color, fontFamily: "'DM Sans', sans-serif" }}
                >
                  {tier?.roi}
                </div>
                <div className="text-white/35 text-sm">{tier?.period}</div>
              </div>

              <div className="space-y-3 mb-6 pb-6 border-b" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                {[
                  { label: "Min. Investment", value: tier?.investment },
                  { label: "Vehicles", value: tier?.vehicles },
                  { label: "Payout Schedule", value: tier?.payout },
                ]?.map((item) => (
                  <div key={item?.label} className="flex justify-between text-sm">
                    <span className="text-white/35">{item?.label}</span>
                    <span className="text-white font-semibold">{item?.value}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-2.5 mb-8">
                {tier?.features?.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-sm">
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs flex-shrink-0" style={{ background: `${tier?.color}20`, color: tier?.color }}>✓</span>
                    <span className="text-white/55">{f}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate("/fleet-finance-and-crowdfunding-screen")}
                className="w-full py-3.5 rounded-2xl font-bold text-sm transition-all duration-300 hover:opacity-90"
                style={
                  tier?.featured
                    ? { background: `linear-gradient(135deg, ${tier?.color}, ${tier?.color}bb)`, color: "#0a1628" }
                    : { background: `${tier?.color}12`, color: tier?.color, border: `1px solid ${tier?.color}28` }
                }
              >
                Start Investing →
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FleetInvestmentSection;
